/**
 * SEARCH-002 — Sous-composants du corps de la palette (⌘K).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` (aucun changement de logique) :
 * `HitItem`, `ActionLinesGroup`, `ScopeFilterChips`, `ContextualCreateBand`,
 * `HelpPill`, `CenteredAction`. Consommés par `SearchPaletteBody`.
 */

import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Info, Plus, Sparkles } from 'lucide-react'
import { CommandGroup, CommandItem } from '@/components/ui/command'
import { type PreviewTarget } from '@/hooks/useSearchPreview'
import { cn } from '@/lib/utils'
import { CATEGORY_REGISTRY, type SearchHit, type SourceType } from '@/lib/searchCategories'
import { SearchHitRow } from './SearchHitRow'
import { type CreatableType, type QuickAction } from './searchPaletteHelpers'

// ---------------------------------------------------------------------------
// Sous-composants
// ---------------------------------------------------------------------------
export function HitItem({
    hit,
    query,
    leading = false,
    onOpen,
    onMobilePreview,
    onHover,
}: {
    hit: SearchHit
    query: string
    leading?: boolean
    onOpen: (hit: SearchHit) => void
    onMobilePreview: (t: PreviewTarget) => void
    onHover: (value: string) => void
}) {
    const { t } = useTranslation()
    const value = `${hit.type}:${hit.id}`

    return (
        <CommandItem value={value} onSelect={() => onOpen(hit)} onMouseEnter={() => onHover(value)} className="gap-2.5">
            {/* Corps visuel PARTAGÉ avec le sélecteur « Relier une fiche » (§3.2). */}
            <SearchHitRow hit={hit} query={query} leadingIcon={leading} />
            {/* Aperçu mobile (44px) — tap = bottom-sheet ; le corps navigue. */}
            <button
                type="button"
                aria-label={t('globalSearch.preview.open_panel')}
                onClick={(e) => {
                    e.stopPropagation()
                    onMobilePreview({ type: hit.type, id: hit.id })
                }}
                className="ml-1 hidden h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent max-md:flex"
            >
                <Info className="h-4 w-4" aria-hidden="true" />
            </button>
        </CommandItem>
    )
}

export function ActionLinesGroup({
    heading,
    actions,
    hasPermission,
    onGo,
}: {
    heading: string
    actions: QuickAction[]
    hasPermission: (slug: string) => boolean
    onGo: (route: string) => void
}) {
    const { t } = useTranslation()
    const allowed = actions.filter((a) => hasPermission(a.permission))
    if (allowed.length === 0) return null
    return (
        <CommandGroup heading={heading}>
            {allowed.map((a) => {
                const Icon = a.icon
                return (
                    <CommandItem
                        key={a.key}
                        value={`action:${a.key}`}
                        data-nav={`action:${a.key}`}
                        onSelect={() => onGo(a.route)}
                    >
                        <Icon className="text-muted-foreground" aria-hidden="true" />
                        {t(`globalSearch.actions.${a.key}`)}
                    </CommandItem>
                )
            })}
        </CommandGroup>
    )
}

export function ScopeFilterChips({
    types,
    onPick,
}: {
    types: readonly SourceType[]
    onPick: (type: SourceType) => void
}) {
    const { t } = useTranslation()
    return (
        <CommandGroup heading={t('globalSearch.filter.title')}>
            <div className="flex flex-wrap gap-2 px-1 pb-1 pt-0.5">
                {types.map((type) => {
                    const cfg = CATEGORY_REGISTRY[type]
                    const Icon = cfg.icon
                    return (
                        <CommandItem
                            key={type}
                            value={`scope:${type}`}
                            data-nav={`scope:${type}`}
                            onSelect={() => onPick(type)}
                            className="inline-flex w-auto gap-1.5 rounded-full border border-border px-2.5 py-1 text-[13px] data-[selected=true]:border-primary/40"
                        >
                            <Icon className="text-muted-foreground" aria-hidden="true" />
                            {t(`globalSearch.group.${cfg.i18nKey}`)}
                        </CommandItem>
                    )
                })}
            </div>
        </CommandGroup>
    )
}

// Création contextuelle (état RÉSULTATS) — PUCES COMPACTES en rangée, précédées de
// « Créer « {q} » » ; pas d'empilement, pas de séparateur, pas de fond gris (§2.1).
// TEAL : la 1ʳᵉ puce (primaire, cible ⌘↵) est rehaussée. Le conteneur est TOUJOURS
// rendu (même sans option) pour garder l'aide alignée à droite (justify-between).
export function ContextualCreateBand({
    options,
    query,
    onGo,
    primaryShortcut,
}: {
    options: CreatableType[]
    query: string
    onGo: (route: string) => void
    /** Indice clavier affiché sur la 1ʳᵉ puce (création primaire, ⌘↵). */
    primaryShortcut?: string
}) {
    const { t } = useTranslation()
    const q = query.trim()
    return (
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">
            {options.length > 0 && (
                <span className="text-[13px] text-muted-foreground">{t('globalSearch.create.prefix', { q })}</span>
            )}
            {options.map((c, i) => (
                <button
                    key={c.type}
                    type="button"
                    // Paramètre DÉDIÉ `create_q` (≠ `q` du filtre de table) → la table cible
                    // n'est PAS filtrée ; la page consomme `create_q` pour le préremplissage.
                    onClick={() => onGo(`${c.route}&create_q=${encodeURIComponent(q)}`)}
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13px] transition-[transform,background-color] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                        i === 0
                            ? 'border-primary/30 bg-primary/5 text-foreground hover:bg-primary/10'
                            : 'border-border text-foreground hover:bg-accent',
                    )}
                >
                    <Plus className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                    {t(`globalSearch.create.short.${c.i18nKey}`)}
                    {i === 0 && primaryShortcut && (
                        <kbd className="ml-0.5 hidden shrink-0 rounded border border-border bg-background px-1 py-0.5 font-mono text-[10px] font-normal text-muted-foreground sm:inline-block">
                            {primaryShortcut}
                        </kbd>
                    )}
                </button>
            ))}
        </div>
    )
}

// Bouton d'AIDE UNIFIÉ (violet `--ai`, étincelle) — un seul canal vers le champ unifié
// du drawer (le drawer tranche article lexical vs réponse IA). Le mot « assistant » est
// banni (§4). `block` → pleine largeur (état vide, centré 2ᵉ colonne) ; sinon auto, à droite.
export function HelpPill({ label, onClick, className }: { label: string; onClick: () => void; className?: string }) {
    return (
        <button
            type="button"
            onClick={onClick}
            // `py-2.5` → cible tactile ~40px (mobile) ; `active:scale` = retour tactile.
            className={cn(
                'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-ai/10 px-3.5 py-2.5 text-[13.5px] font-medium text-ai transition-[transform,background-color] hover:bg-ai/15 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai',
                className,
            )}
        >
            <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{label}</span>
        </button>
    )
}

// Action centrée (pleine forme « pilule ») de l'état « aucun résultat » : variante
// `primary` (création mise en avant), `ai` (assistant, violet) ou contour neutre (aide).
// Navigable au clavier : `dataNav` = cible de la navigation 2D ; `selected` → bague.
export function CenteredAction({
    icon: Icon,
    onClick,
    onHover,
    variant = 'default',
    selected = false,
    dataNav,
    children,
}: {
    icon: typeof Plus
    onClick: () => void
    onHover?: () => void
    variant?: 'default' | 'ai' | 'primary'
    selected?: boolean
    dataNav?: string
    children: ReactNode
}) {
    return (
        <button
            type="button"
            data-nav={dataNav}
            onClick={onClick}
            onMouseEnter={onHover}
            className={cn(
                // `min-h-[44px]` → cible tactile conforme (actions primaires « aucun résultat »).
                'inline-flex min-h-[44px] max-w-full items-center justify-center gap-2 rounded-md border px-3.5 py-2.5 text-[13.5px] font-medium transition-[transform,background-color] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                variant === 'ai'
                    ? 'border-ai/30 text-ai hover:bg-ai/10'
                    : variant === 'primary'
                      ? 'border-transparent bg-primary text-primary-foreground hover:opacity-90'
                      : 'border-border text-foreground hover:bg-accent',
                // Sélection clavier (flèches) : bague nette, lisible sur toutes les variantes.
                selected && 'ring-2 ring-primary ring-offset-2 ring-offset-popover',
            )}
        >
            <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{children}</span>
        </button>
    )
}

/**
 * Sous la liste : le serveur a abandonné la correction des fautes faute de temps
 * (`typos_skipped`). Les fiches montrées sont celles trouvées SANS faute ; il en manque
 * peut-être, et la réponse utile est de préciser la saisie.
 */
export function TyposSkippedNote({ className }: { className?: string }) {
    const { t } = useTranslation()
    return (
        <p className={cn('text-xs text-muted-foreground', className ?? 'border-t border-border px-3 py-2')}>
            {t('globalSearch.state.typos_skipped')}
        </p>
    )
}
